// Focus optimization utilities for TV remote control

// Debounce for keyboard navigation
export function createNavigationDebouncer(delay = 50) {
  let timeoutId: number | null = null;
  let lastDirection: string | null = null;
  let isProcessing = false;

  return {
    debounce(direction: string, callback: () => void) {
      // If same direction pressed rapidly, debounce it
      if (lastDirection === direction && timeoutId !== null) {
        clearTimeout(timeoutId);
      }

      lastDirection = direction;

      // Prevent processing if already handling a navigation
      if (isProcessing) {
        return;
      }

      timeoutId = window.setTimeout(() => {
        isProcessing = true;
        callback();

        // Reset processing flag after animation
        requestAnimationFrame(() => {
          isProcessing = false;
        });

        timeoutId = null;
        lastDirection = null;
      }, delay);
    },

    cancel() {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      lastDirection = null;
      isProcessing = false;
    },

    isDebouncing() {
      return timeoutId !== null || isProcessing;
    },
  };
}

// Cache for DOM queries
class FocusElementCache {
  private cache = new Map<string, { elements: HTMLElement[]; ts: number }>();
  private rootCache = new WeakMap<ParentNode, Map<string, { elements: HTMLElement[]; ts: number }>>();
  private readonly maxAge = 1000; // 1 second TTL

  // Get elements for a selector. If `root` provided, use a scoped WeakMap cache
  get(selector: string, root?: ParentNode): HTMLElement[] {
    const now = Date.now();

    if (root) {
      let map = this.rootCache.get(root);
      if (!map) {
        map = new Map();
        this.rootCache.set(root, map);
      }

      const entry = map.get(selector);
      if (entry && now - entry.ts <= this.maxAge) return entry.elements;

      const elements = Array.from(root.querySelectorAll<HTMLElement>(selector));
      map.set(selector, { elements, ts: now });
      return elements;
    }

    const entry = this.cache.get(selector);
    if (entry && now - entry.ts <= this.maxAge) return entry.elements;

    const elements = Array.from(document.querySelectorAll<HTMLElement>(selector));
    this.cache.set(selector, { elements, ts: now });
    return elements;
  }

  invalidate() {
    this.cache.clear();
    this.rootCache = new WeakMap();
  }
}

// Smooth scroll with RAF
export function smoothScrollTo(element: HTMLElement, targetScrollLeft: number, duration = 200): Promise<void> {
  return new Promise(resolve => {
    const startScrollLeft = element.scrollLeft;
    const distance = targetScrollLeft - startScrollLeft;
    const startTime = performance.now();

    function animation(currentTime: number) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function for smooth animation
      const easeProgress = 1 - Math.pow(1 - progress, 3);

      element.scrollLeft = startScrollLeft + distance * easeProgress;

      if (progress < 1) {
        requestAnimationFrame(animation);
      } else {
        resolve();
      }
    }

    requestAnimationFrame(animation);
  });
}

// Optimized scroll to center for cards
export function scrollToCenter(container: HTMLElement, target: HTMLElement, smooth = true): void {
  if (!container || !target) return;

  const containerRect = container.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();

  const scrollLeft = container.scrollLeft;
  const containerCenter = containerRect.width / 2;
  const targetCenter = targetRect.left - containerRect.left + targetRect.width / 2;

  const targetScrollLeft = scrollLeft + targetCenter - containerCenter;

  if (smooth) {
    smoothScrollTo(container, targetScrollLeft);
  } else {
    container.scrollLeft = targetScrollLeft;
  }
}

// Performance-optimized focus state manager
export class OptimizedFocusManager {
  private focusedElement: HTMLElement | null = null;
  private willChangeTimeout: number | null = null;

  setFocus(element: HTMLElement) {
    // Remove focus from previous element
    if (this.focusedElement && this.focusedElement !== element) {
      this.removeFocusOptimizations(this.focusedElement);
    }

    // Add focus to new element
    this.addFocusOptimizations(element);
    this.focusedElement = element;
  }

  private addFocusOptimizations(element: HTMLElement) {
    // Add will-change before animation starts
    element.style.willChange = 'transform';

    // Force a reflow to ensure will-change takes effect
    element.offsetHeight;

    // Add focused class
    element.classList.add('focused');

    // Remove will-change after animation completes
    if (this.willChangeTimeout) {
      clearTimeout(this.willChangeTimeout);
    }

    this.willChangeTimeout = window.setTimeout(() => {
      if (element.style.willChange === 'transform') {
        element.style.willChange = 'auto';
      }
    }, 250); // Slightly longer than transition duration
  }

  private removeFocusOptimizations(element: HTMLElement) {
    element.classList.remove('focused');
    element.style.willChange = 'auto';
  }

  cleanup() {
    if (this.willChangeTimeout) {
      clearTimeout(this.willChangeTimeout);
    }
    if (this.focusedElement) {
      this.removeFocusOptimizations(this.focusedElement);
    }
  }
}

// Export singleton instances
export const navigationDebouncer = createNavigationDebouncer(50);
export const focusElementCache = new FocusElementCache();
export const optimizedFocusManager = new OptimizedFocusManager();
